/**
 * Notification Filtering Utilities
 * Handles filtering and processing of notification data
 */

import type { DrupalNotification, NotificationVariant } from '../types';
import { NOTIFICATION_SEVERITY_RANK } from '../types';

/**
 * Extracts notified user emails from field_notified_users
 * Handles various data structures from Drupal
 */
export function getNotifiedEmails(notification: DrupalNotification): string[] {
  const raw = notification.field_notified_users;
  const toArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : v ? [v] : []);
  const arr = toArray(raw);
  const emails: string[] = [];

  for (const it of arr) {
    try {
      let payload: unknown = it;

      // Handle {value: string} wrapper
      if (payload && typeof payload === 'object' && 'value' in (payload as Record<string, unknown>)) {
        const v = (payload as { value?: unknown }).value;
        payload = typeof v === 'string' ? JSON.parse(v) : v;
      } else if (typeof payload === 'string') {
        payload = JSON.parse(payload);
      }

      // Extract email from parsed object
      const em = (payload as { email?: string })?.email;
      if (em) emails.push(String(em).toLowerCase());
    } catch {
      // Skip invalid entries
    }
  }

  return emails;
}

/**
 * Checks if a user has been notified (email is in notified list)
 */
export function isUserNotified(notification: DrupalNotification, userEmail: string | null): boolean {
  if (!userEmail) return false;
  const notifiedEmails = getNotifiedEmails(notification);
  return notifiedEmails.includes(String(userEmail).toLowerCase());
}

/**
 * Filters notifications to exclude those already seen by the user
 */
export function filterUnseenNotifications(
  notifications: DrupalNotification[],
  userEmail: string | null,
  hiddenIds: Set<string> = new Set()
): DrupalNotification[] {
  const email = String(userEmail || '').toLowerCase();

  return notifications.filter(notification => {
    const id = String(notification.id || '');
    if (!id || hiddenIds.has(id)) return false;
    if (!email) return true;

    return !isUserNotified(notification, email);
  });
}

/**
 * Extracts notification variant with fallback
 */
export function getNotificationVariant(notification: DrupalNotification): NotificationVariant {
  const variant = String(notification.field_variant || '').toLowerCase();
  if (variant === 'alert' || variant === 'warning' || variant === 'info' || variant === 'success') {
    return variant as NotificationVariant;
  }
  return 'info';
}

/**
 * Calculates the badge colour class based on highest severity notification
 */
export function calculateBadgeClass(notifications: DrupalNotification[]): string {
  let maxSeverity = 0;
  let badgeClass = 'bg-red-600';

  for (const notification of notifications) {
    const variant = getNotificationVariant(notification);
    const rank = NOTIFICATION_SEVERITY_RANK[variant] || 0;

    if (rank > maxSeverity) {
      maxSeverity = rank;
      badgeClass =
        variant === 'alert'
          ? 'bg-red-600'
          : variant === 'warning'
            ? 'bg-amber-500'
            : variant === 'info'
              ? 'bg-blue-600'
              : 'bg-emerald-600';
    }
  }

  return badgeClass;
}

/**
 * Extracts HTML content from notification with fallback
 */
export function getNotificationHtml(notification: DrupalNotification): string {
  const htmlObj = notification.field_html || notification.attributes?.field_html;

  if (!htmlObj) return '';

  if (typeof htmlObj === 'object') {
    const processed = (htmlObj as { processed?: unknown }).processed;
    const value = (htmlObj as { value?: unknown }).value;

    return String(processed || value || '');
  }

  return String(htmlObj || '');
}

/**
 * Extracts notification title
 */
export function getNotificationTitle(notification: DrupalNotification): string {
  return String(notification.title || '');
}

/**
 * Parses date string with fallback
 */
export function parseNotificationDate(dateString: string | undefined | null): Date | null {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Checks if notification is currently active based on date range
 */
export function isNotificationActive(notification: DrupalNotification, now: Date = new Date()): boolean {
  const startDate = parseNotificationDate(notification.field_start);
  const endDate = parseNotificationDate(notification.field_end);

  if (startDate && now < startDate) return false;
  if (endDate && now > endDate) return false;

  return true;
}

/**
 * Merges notification arrays and deduplicates by ID
 */
export function mergeNotifications(
  ...notificationArrays: (DrupalNotification[] | undefined | null)[]
): DrupalNotification[] {
  const map = new Map<string, DrupalNotification>();

  for (const array of notificationArrays) {
    if (!Array.isArray(array)) continue;

    for (const notification of array) {
      const id = String(notification.id || '');
      if (id && !map.has(id)) {
        map.set(id, notification);
      }
    }
  }

  return Array.from(map.values());
}

